import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@zaogig/auth';
import { toast } from 'sonner';
import '../styles/interview-preparation.css';

interface InterviewPreparationProps {
  jobId: string;
  interviewId?: string;
}

export function InterviewPreparation({ jobId }: InterviewPreparationProps) {
  const [interviewPreparation, setInterviewPreparation] = useState<any>(null);
  const [interviewTips, setInterviewTips] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'questions' | 'company' | 'technical' | 'behavioral'>('questions');
  const [isLoading, setIsLoading] = useState(false);

  const loadPreparationData = useCallback(async () => {
    try {
      setIsLoading(true);

      const [prepData, tipsData] = await Promise.all([
        apiClient.get<any>('/v1/interview/preparation/' + jobId),
        apiClient.get<any>('/v1/interview/tips')
      ]);

      setInterviewPreparation(prepData);
      setInterviewTips(tipsData);
    } catch (error) {
      console.error('加载准备材料失败:', error);
      toast.error('加载准备材料失败');
    } finally {
      setIsLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    loadPreparationData();
  }, [loadPreparationData]);

  const renderTabButton = (key: typeof activeTab, label: string) => (
    <div 
      className={`tab-button ${activeTab === key ? 'active' : ''}`}
      onClick={() => setActiveTab(key)}
    >
      <p className="tab-text">{label}</p>
    </div>
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="loading-content">
          <p className="loading-text">加载中...</p>
        </div>
      );
    }

    switch (activeTab) {
      case 'questions':
        return renderCommonQuestions();
      case 'company':
        return renderCompanyTips();
      case 'technical':
        return renderTechnicalTips();
      case 'behavioral':
        return renderBehavioralTips();
      default:
        return null;
    }
  };

  const renderCommonQuestions = () => {
    const questions = interviewPreparation?.commonQuestions || interviewTips?.general || [];
    
    return (
      <div className="content-section">
        <p className="section-title">常见面试问题</p>
        <div className="questions-list">
          {questions.length > 0 ? questions.map((question: string, index: number) => (
            <div key={index} className="question-item">
              <div className="question-number">Q{index + 1}</div>
              <p className="question-text">{question}</p>
            </div>
          )) : (
            <p className="empty-text">暂无常见问题</p>
          )}
        </div>
      </div>
    );
  };

  const renderCompanyTips = () => {
    const tips = interviewPreparation?.companyTips || [];
    
    return (
      <div className="content-section">
        <p className="section-title">公司了解要点</p>
        <div className="tips-list">
          {tips.length > 0 ? tips.map((tip: string, index: number) => (
            <div key={index} className="tip-item">
              <div className="tip-icon">💡</div>
              <p className="tip-text">{tip}</p>
            </div>
          )) : (
            <p className="empty-text">暂无公司相关信息</p>
          )}
        </div>
      </div>
    );
  };

  const renderTechnicalTips = () => {
    const tips = interviewPreparation?.technicalTips || interviewTips?.technical || [];
    
    return (
      <div className="content-section">
        <p className="section-title">技术面试要点</p>
        <div className="tips-list">
          {tips.length > 0 ? tips.map((tip: string, index: number) => (
            <div key={index} className="tip-item">
              <div className="tip-icon">🔧</div>
              <p className="tip-text">{tip}</p>
            </div>
          )) : (
            <p className="empty-text">暂无技术面试要点</p>
          )}
        </div>
      </div>
    );
  };

  const renderBehavioralTips = () => {
    const tips = interviewPreparation?.behavioralTips || interviewTips?.behavioral || [];
    
    return (
      <div className="content-section">
        <p className="section-title">行为面试技巧</p>
        <div className="tips-list">
          {tips.length > 0 ? tips.map((tip: string, index: number) => (
            <div key={index} className="tip-item">
              <div className="tip-icon">🤝</div>
              <p className="tip-text">{tip}</p>
            </div>
          )) : (
            <p className="empty-text">暂无行为面试技巧</p>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="interview-preparation">
      <div className="preparation-header">
        <p className="header-title">面试准备</p>
        <p className="header-subtitle">充分准备，成功面试</p>
      </div>

      <div className="tab-bar">
        {renderTabButton('questions', '常见问题')}
        {renderTabButton('company', '公司了解')}
        {renderTabButton('technical', '技术要点')}
        {renderTabButton('behavioral', '行为技巧')}
      </div>

      <div className="content-scroll">
        {renderContent()}

        <div className="reminder-section">
          <p className="reminder-title">面试前一天</p>
          <div className="reminder-list">
            <p className="reminder-item">✓ 准备好身份证和相关证书</p>
            <p className="reminder-item">✓ 规划好路线，预留充足时间</p>
            <p className="reminder-item">✓ 准备好求职信和作品集</p>
            <p className="reminder-item">✓ 保证充足睡眠，保持良好状态</p>
          </div>
        </div>

        <div className="reminder-section">
          <p className="reminder-title">面试当天</p>
          <div className="reminder-list">
            <p className="reminder-item">✓ 提前30分钟到达面试地点</p>
            <p className="reminder-item">✓ 着装整洁，展现专业形象</p>
            <p className="reminder-item">✓ 手机调至静音，保持专注</p>
            <p className="reminder-item">✓ 保持自信，积极沟通</p>
          </div>
        </div>

        <div className="emergency-section">
          <p className="emergency-title">紧急联系</p>
          <div className="emergency-info">
            <p className="emergency-item">📞 HR联系电话：400-123-4567</p>
            <p className="emergency-item">📧 邮箱：hr@company.com</p>
            <p className="emergency-item">🕐 工作时间：周一至周五 9:00-18:00</p>
          </div>
        </div>
      </div>

      <div className="preparation-footer">
        <button className="calendar-btn">
          📅 添加到日历
        </button>
        <button className="share-btn">
          📤 分享准备材料
        </button>
      </div>
    </div>
  );
}
